import React from 'react';
import styled from 'styled-components';
import { FiX, FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { colors, typography } from '../../theme/colors';
import { formatAmount } from '../../utils/documentNumbering';
import { navigateToTransaction } from '../../utils/navigationHelper';

// Right-side detail panel. Shows full header + lines + payment progress for
// the currently selected node. Two action buttons: open the source page
// (existing list view) and close.
const CycleSidePanel = ({ node, onClose, currencyCode }) => {
  const navigate = useNavigate();
  if (!node) return null;
  const sym = currencyCode || 'PKR';

  const isInvoice = ['PI', 'NPI', 'IPI', 'SI', 'NSI'].includes(node.tranTypeCode);
  const isPayment = ['CP', 'BP', 'CR', 'BR'].includes(node.tranTypeCode);

  const openSource = () => {
    navigateToTransaction(navigate, node.tranTypeCode, node.id);
    onClose && onClose();
  };

  return (
    <Panel>
      <Header>
        <Title>
          <Code>{node.tranTypeCode}</Code>
          <span>{node.docNumber}</span>
        </Title>
        <Close onClick={onClose}><FiX size={16} /></Close>
      </Header>

      <ActionsRow>
        <PrimaryBtn onClick={openSource}>
          <FiExternalLink size={12} /> Open document
        </PrimaryBtn>
      </ActionsRow>

      <Section>
        <SectionLabel>Header</SectionLabel>
        <Row><span>Date</span><strong>{node.docDate || '—'}</strong></Row>
        <Row><span>Status</span><strong>{node.status || '—'}</strong></Row>
        <Row><span>{node.partyType === 'vendor' ? 'Vendor' : (node.partyType === 'customer' ? 'Customer' : 'Party')}</span><strong>{node.partyName || '—'}</strong></Row>
        {node.branchName && <Row><span>Branch</span><strong>{node.branchName}</strong></Row>}
        <Row><span>Total</span><strong>{sym} {formatAmount(node.totalAmount)}</strong></Row>
        {isInvoice && (
          <>
            <Row><span>Paid</span><strong style={{ color: colors.success.main }}>{sym} {formatAmount(node.paidAmount)}</strong></Row>
            <Row><span>Balance</span><strong style={{ color: node.balanceAmount > 0 ? colors.error.main : colors.text.secondary }}>{sym} {formatAmount(node.balanceAmount)}</strong></Row>
          </>
        )}
      </Section>

      {isPayment && (node.invoiceAllocations || []).length > 0 && (
        <Section>
          <SectionLabel>Allocated to</SectionLabel>
          {node.invoiceAllocations.map((alloc, i) => (
            <Row key={i}>
              <span>{alloc.docNumber}</span>
              <strong>{sym} {formatAmount(alloc.allocatedAmount)}</strong>
            </Row>
          ))}
        </Section>
      )}

      {(node.lines || []).length > 0 && (
        <Section>
          <SectionLabel>Lines ({node.lines.length})</SectionLabel>
          <LinesScroll>
            {node.lines.map((line, i) => (
              <LineCard key={i}>
                <LineTop>
                  <LineName title={line.itemName || line.description}>
                    {line.itemName || line.description || '—'}
                  </LineName>
                  <LineQty>
                    {line.quantity || 0} {line.unitOfMeasure || ''}
                  </LineQty>
                </LineTop>
                <LineBottom>
                  <span>@ {formatAmount(line.unitPrice || line.unitCost || 0)}</span>
                  <strong>{formatAmount(line.lineTotal || 0)}</strong>
                </LineBottom>
              </LineCard>
            ))}
          </LinesScroll>
        </Section>
      )}
    </Panel>
  );
};

export default CycleSidePanel;

const Panel = styled.div`
  width: 320px;
  flex-shrink: 0;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
`;
const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 700;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.mono};
`;
const Code = styled.span`
  font-size: 11px; font-weight: 700; letter-spacing: 0.4px;
  padding: 2px 6px; border-radius: 3px;
  background: ${colors.primary.main}18; color: ${colors.primary.main};
`;
const Close = styled.button`
  background: transparent; border: none; cursor: pointer;
  color: ${colors.text.secondary};
  display: inline-flex;
  &:hover { color: ${colors.error.main}; }
`;
const ActionsRow = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  &:hover { background: ${colors.primary.dark || colors.primary.main}; }
`;
const Section = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  &:last-child { border-bottom: none; }
`;
const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
  margin-bottom: 8px;
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
  color: ${colors.text.secondary};
  font-weight: 500;
  strong {
    color: ${colors.text.primary};
    font-weight: 700;
    font-family: ${typography.fontFamily.mono};
    text-align: right;
  }
`;
const LinesScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  max-height: 280px;
  overflow-y: auto;
`;
const LineCard = styled.div`
  padding: 8px 10px;
  background: ${colors.background?.paper || '#fafafa'};
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
const LineTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
`;
const LineName = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const LineQty = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
  font-family: ${typography.fontFamily.mono};
`;
const LineBottom = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${colors.text.secondary};
  font-weight: 500;
  strong {
    color: ${colors.text.primary};
    font-weight: 700;
    font-family: ${typography.fontFamily.mono};
  }
`;
